import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Iconography,
  IconographySizes,
  InlineStatus,
  InlineStatusAppearance,
  InlineStatusIconType,
  Pagination,
} from "@hid-galaxy-ui/galaxy-react";
import axios from "axios";
import "./HealthCheckHistory.scss";

interface HealthCheckup {
  apiStatusID: string;
  feature: string;
  endPoints: string;
  statusCode: number;
  statusMessage: string;
  responseTime: string;
  updatedAt: string;
}

interface HealthCheckHistoryProps {}

const HealthCheckHistory: React.FC<HealthCheckHistoryProps> = () => {
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState<HealthCheckup[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedIteration, setSelectedIteration] = useState<{
    timestamp: string;
    data: HealthCheckup[];
    iterationNumber: number;
  } | null>(null);
  const [iterationFilter, setIterationFilter] = useState({
    category: "",
    status: "",
    searchText: "",
  });
  const [detailsPageNumber, setDetailsPageNumber] = useState<number>(1);
  const [detailsPageSize] = useState<number>(10);

  const openIterationDetails = (
    timestamp: string,
    data: HealthCheckup[],
    iterationNumber: number
  ) => {
    setSelectedIteration({ timestamp, data, iterationNumber });
    setIterationFilter({ category: "", status: "", searchText: "" });
    setDetailsPageNumber(1);
  };

  const closeIterationDetails = () => {
    setSelectedIteration(null);
    setIterationFilter({ category: "", status: "", searchText: "" });
  };

  // Filter iteration details
  const getFilteredIterationData = () => {
    if (!selectedIteration) return { data: [], total: 0 };

    const filtered = selectedIteration.data.filter((item) => {
      const matchesCategory =
        !iterationFilter.category || item.feature === iterationFilter.category;
      const matchesStatus =
        !iterationFilter.status ||
        (iterationFilter.status === "success"
          ? item.statusCode >= 200 && item.statusCode < 300
          : item.statusCode < 200 || item.statusCode >= 300);
      const matchesSearch =
        !iterationFilter.searchText ||
        item.endPoints
          ?.toLowerCase()
          .includes(iterationFilter.searchText.toLowerCase());

      return matchesCategory && matchesStatus && matchesSearch;
    });

    const total = filtered.length;
    const startIndex = (detailsPageNumber - 1) * detailsPageSize;
    const endIndex = startIndex + detailsPageSize;
    const paginatedData = filtered.slice(startIndex, endIndex);

    return { data: paginatedData, total };
  };

  useEffect(() => {
    fetchFeatureSupportedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFeatureSupportedData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/api-status`
      );
      console.log("✅ Health Check History Data:", response.data);

      // Set the data from API response - always use fresh data from API
      if (Array.isArray(response.data)) {
        setHistoryData(response.data);
      } else {
        setHistoryData([]);
      }
    } catch (error: any) {
      console.error("❌ Error fetching api-status data:", error.message);
      if (error.response) {
        console.error("Response error:", error.response.data);
      }
      setHistoryData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Sort by timestamp (most recent first)
  const sortedData = [...historyData].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

  // Group data by exact date and minute timestamp (e.g., Dec 26 1:02 PM, Dec 26 1:03 PM)
  const groupedByTimestamp = sortedData.reduce((acc, item) => {
    const timestamp = new Date(item.updatedAt);

    // Keep the full date and time, but round to the exact minute (remove seconds and milliseconds)
    timestamp.setSeconds(0, 0);

    // Create a unique key that includes both date and time down to the minute
    const timeKey = timestamp.toISOString();

    if (!acc[timeKey]) {
      acc[timeKey] = [];
    }

    // Add all API records to the group (show all data from API)
    acc[timeKey].push(item);

    return acc;
  }, {} as Record<string, HealthCheckup[]>);

  const timestampKeys = Object.keys(groupedByTimestamp);
  const totalRecords = timestampKeys.length;
  const paginatedTimestamps = timestampKeys.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  const getStatusBadge = (statusCode: number, success: boolean) => {
    if (statusCode >= 200 && statusCode < 300) {
      return (
        <InlineStatus
          label={statusCode.toString()}
          appearance={InlineStatusAppearance.Positive}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else if (statusCode >= 400 && statusCode < 500) {
      return (
        <InlineStatus
          label={statusCode.toString()}
          appearance={InlineStatusAppearance.Caution}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else if (statusCode >= 500) {
      return (
        <InlineStatus
          label={statusCode.toString()}
          appearance={InlineStatusAppearance.Danger}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else {
      return (
        <InlineStatus
          label={statusCode.toString()}
          appearance={InlineStatusAppearance.Info}
          iconType={InlineStatusIconType.Dot}
        />
      );
    }
  };

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      Authentication: "#00aeef",
      "User Management": "#94bb20",
      Partner: "#f8971d",
      Incident: "#d31245",
      Feature: "#8b4789",
      Invitation: "#ff6b35",
      Credentials: "#6c5ce7",
      "Part Number": "#e91e63",
    };
    const color = colors[category] || "#666666";
    return (
      <span
        className="category-badge"
        style={{
          backgroundColor: `${color}20`,
          color: color,
          border: `1px solid ${color}`,
        }}
      >
        {category}
      </span>
    );
  };

  const getSuccessIndicator = (statusCode: number) => {
    const isSuccess = statusCode >= 200 && statusCode < 300;
    if (isSuccess) {
      return (
        <span className="success-indicator success">
          <Iconography icon="circleCheck" size={IconographySizes.Small} />
          Success
        </span>
      );
    } else {
      return (
        <span className="success-indicator failure">
          <Iconography icon="circleXmark" size={IconographySizes.Small} />
          Failed
        </span>
      );
    }
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const getResponseTimeClass = (responseTime: string) => {
    // Extract numeric value from responseTime (e.g., "120ms" -> 120)
    const timeInMs = parseInt(responseTime.replace("ms", ""));

    if (timeInMs < 1000) return "fast";
    if (timeInMs < 2000) return "normal";
    return "slow"; // Red color for >= 2000ms (2 seconds)
  };

  const formatResponseTime = (responseTime: string) => {
    // Convert milliseconds to seconds (e.g., "120ms" -> "0.12s")
    const timeInMs = parseFloat(responseTime.replace("ms", ""));

    // Handle invalid or NaN values
    if (isNaN(timeInMs) || timeInMs === null || timeInMs === undefined) {
      return "0s";
    }

    const timeInSeconds = (timeInMs / 1000).toFixed(2);
    return `${timeInSeconds}s`;
  };

  return (
    <div className="health-history-screen">
      <div className="health-history-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Iconography icon="arrowLeft" size={IconographySizes.Medium} />
          Back
        </button>
        <h1 className="page-title">Health Check History</h1>
        <button
          className="refresh-btn"
          onClick={fetchFeatureSupportedData}
          disabled={isLoading}
        >
          <Iconography icon="arrowsRotate" size={IconographySizes.Medium} />
          {isLoading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="health-history-container">
        {/* Statistics Cards */}
        <div className="history-stats-cards">
          <div className="stat-card total">
            <div className="stat-icon">
              <Iconography icon="chartLine" size={IconographySizes.Large} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total APIs</div>
              <div className="stat-value">{historyData.length}</div>
            </div>
          </div>

          <div className="stat-card success">
            <div className="stat-icon">
              <Iconography icon="circleCheck" size={IconographySizes.Large} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Success APIs</div>
              <div className="stat-value">
                {
                  historyData.filter(
                    (item) => item.statusCode >= 200 && item.statusCode < 300
                  ).length
                }
              </div>
            </div>
          </div>

          <div className="stat-card failed">
            <div className="stat-icon">
              <Iconography icon="circleXmark" size={IconographySizes.Large} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Failed APIs</div>
              <div className="stat-value">
                {
                  historyData.filter(
                    (item) => item.statusCode < 200 || item.statusCode >= 300
                  ).length
                }
              </div>
            </div>
          </div>

          <div className="stat-card rate">
            <div className="stat-icon">
              <Iconography icon="percent" size={IconographySizes.Large} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Success Rate</div>
              <div className="stat-value">
                {historyData.length > 0
                  ? Math.round(
                      (historyData.filter(
                        (item) =>
                          item.statusCode >= 200 && item.statusCode < 300
                      ).length /
                        historyData.length) *
                        100
                    )
                  : 0}
                %
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="history-table-container">
          {paginatedTimestamps.length > 0 ? (
            <div className="iterations-list">
              <table className="iterations-table">
                <thead>
                  <tr>
                    <th>Iteration</th>
                    <th>Date & Time</th>
                    <th>APIs Checked</th>
                    <th>Success</th>
                    <th>Failed</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTimestamps.map((timestamp, index) => {
                    const items = groupedByTimestamp[timestamp];
                    const successCount = items.filter(
                      (item) => item.statusCode >= 200 && item.statusCode < 300
                    ).length;
                    const failedCount = items.length - successCount;
                    const iterationNumber =
                      timestampKeys.indexOf(timestamp) + 1;
                    // Use the first item's timestamp for display
                    const displayTimestamp = items[0].updatedAt;

                    return (
                      <tr key={timestamp}>
                        <td className="iteration-number">
                          <span className="iteration-badge">
                            #{iterationNumber}
                          </span>
                        </td>
                        <td className="date-time-cell">
                          <div className="date-time-wrapper">
                            <Iconography
                              icon="calendar"
                              size={IconographySizes.Small}
                            />
                            <span>{formatDate(displayTimestamp)}</span>
                          </div>
                          <div className="time-wrapper">
                            <Iconography
                              icon="clock"
                              size={IconographySizes.Small}
                            />
                            <span>{formatTime(displayTimestamp)}</span>
                            <span className="time-ago">
                              ({getTimeAgo(displayTimestamp)})
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="count-badge total">
                            {items.length}
                          </span>
                        </td>
                        <td>
                          <span className="count-badge success">
                            {successCount}
                          </span>
                        </td>
                        <td>
                          <span className="count-badge failed">
                            {failedCount}
                          </span>
                        </td>
                        <td className="action-cell">
                          <button
                            className="view-details-btn"
                            onClick={() =>
                              openIterationDetails(
                                displayTimestamp,
                                items,
                                iterationNumber
                              )
                            }
                          >
                            <Iconography
                              icon="arrowRight"
                              size={IconographySizes.Small}
                            />
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <Iconography
                  icon="clockRotateLeft"
                  size={IconographySizes.XXLarge}
                />
              </div>
              <h3>No History Records Found</h3>
              <p>
                No health check history available yet. Click Refresh to load the
                latest data.
              </p>
            </div>
          )}
        </div>

        {/* Pagination for Iterations List */}
        {totalRecords > pageSize && (
          <div className="pagination-section">
            <Pagination
              currentPageNumber={currentPageNumber}
              totalRecords={totalRecords}
              pageSize={pageSize}
              onPageNumberClick={(page: number | null) => {
                if (page) setCurrentPageNumber(page);
              }}
            />
          </div>
        )}
      </div>

      {/* Iteration Details Panel - Separate Overlay */}
      {selectedIteration && (
        <div
          className="iteration-details-overlay"
          onClick={closeIterationDetails}
        >
          <div
            className="iteration-details-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-header">
              <div className="header-content">
                <h3>Iteration #{selectedIteration.iterationNumber} Details</h3>
                <div className="iteration-meta">
                  <Iconography icon="calendar" size={IconographySizes.Small} />
                  <span>{formatDate(selectedIteration.timestamp)}</span>
                  <span className="separator">•</span>
                  <Iconography icon="clock" size={IconographySizes.Small} />
                  <span>{formatTime(selectedIteration.timestamp)}</span>
                  <span className="time-ago">
                    ({getTimeAgo(selectedIteration.timestamp)})
                  </span>
                </div>
              </div>
              <button className="close-btn" onClick={closeIterationDetails}>
                <Iconography icon="xmark" size={IconographySizes.Medium} />
              </button>
            </div>

            <div className="panel-filters">
              <div className="filter-group">
                <label>Search API</label>
                <input
                  type="text"
                  placeholder="Search by endpoint..."
                  value={iterationFilter.searchText}
                  onChange={(e) =>
                    setIterationFilter({
                      ...iterationFilter,
                      searchText: e.target.value,
                    })
                  }
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>Category</label>
                <select
                  value={iterationFilter.category}
                  onChange={(e) =>
                    setIterationFilter({
                      ...iterationFilter,
                      category: e.target.value,
                    })
                  }
                  className="filter-select"
                >
                  <option value="">All Categories</option>
                  {Array.from(
                    new Set(selectedIteration.data.map((d) => d.feature))
                  ).map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Status</label>
                <select
                  value={iterationFilter.status}
                  onChange={(e) =>
                    setIterationFilter({
                      ...iterationFilter,
                      status: e.target.value,
                    })
                  }
                  className="filter-select"
                >
                  <option value="">All Status</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>

            <div className="panel-content">
              <table className="iteration-details-table">
                <thead>
                  <tr>
                    <th>API Endpoint</th>
                    <th>Category</th>
                    <th>Status Code</th>
                    <th>Response Time</th>
                    <th>Success</th>
                    <th>Error Details</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredIterationData().data.map((item) => {
                    const isSuccess =
                      item.statusCode >= 200 && item.statusCode < 300;
                    return (
                      <tr
                        key={item.apiStatusID}
                        className={isSuccess ? "success-row" : "failed-row"}
                      >
                        <td>
                          <div className="api-cell">
                            <Iconography
                              icon="code"
                              size={IconographySizes.Small}
                            />
                            <code className="api-code" title={item.endPoints}>
                              {item.endPoints}
                            </code>
                          </div>
                        </td>
                        <td>{getCategoryBadge(item.feature)}</td>
                        <td>{getStatusBadge(item.statusCode, isSuccess)}</td>
                        <td>
                          <span
                            className={`response-time ${getResponseTimeClass(
                              item.responseTime
                            )}`}
                          >
                            <Iconography
                              icon="stopwatch"
                              size={IconographySizes.Small}
                            />
                            {formatResponseTime(item.responseTime)}
                          </span>
                        </td>
                        <td>{getSuccessIndicator(item.statusCode)}</td>
                        <td>
                          {item.statusMessage && !isSuccess ? (
                            <div
                              className="error-cell"
                              title={item.statusMessage}
                            >
                              <Iconography
                                icon="triangleExclamation"
                                size={IconographySizes.Small}
                              />
                              <span className="error-text">
                                {item.statusMessage.length > 40
                                  ? `${item.statusMessage.substring(0, 40)}...`
                                  : item.statusMessage}
                              </span>
                            </div>
                          ) : (
                            <span className="no-error">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination for Details Modal */}
              {getFilteredIterationData().total > detailsPageSize && (
                <div className="details-pagination-section">
                  <Pagination
                    currentPageNumber={detailsPageNumber}
                    totalRecords={getFilteredIterationData().total}
                    pageSize={detailsPageSize}
                    onPageNumberClick={(page: number | null) => {
                      if (page) setDetailsPageNumber(page);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthCheckHistory;
